import React, { ChangeEvent, Dispatch, useEffect, useRef, useState } from 'react'
import { Policy } from './OrganizationAddUserTypes'

import loadLightIcon from '../../images/icons/load-light.gif'
import loadDarkIcon from '../../images/icons/load-dark.gif'
import { useTheme } from '../../context/ThemeContext'
import { DownIcon, UpIcon } from '../iconSet/IconSet'

type OrganizationPolicyDataTablePropsType = {
    data: Policy[];
    handleSelectRow: (selected: Policy[]) => void;
    selectedPolicies: string[];
    isDataLoading: boolean
}

const OrganizationPolicyDataTable = (props: OrganizationPolicyDataTablePropsType) => {

    const { theme } = useTheme()
    
    const [selectedCodes, setSelectedCodes] = useState(new Set());
    const rowNumber = useRef<HTMLInputElement | null>(null)
    const searchText = useRef<HTMLInputElement | null>(null)

    const [selectedPage, setSelectedPage] = useState<number>(0)
    const [sliceCount, setSliceCount] = useState<number>(10)

    const [filteredData, setFilteredData] = useState<Policy[]>([])
    const [slicedData, setSlicedData] = useState<Policy[][]>([])

    // true = A -> Z
    // false = Z -> A
    const [policySort, setPolicySort] = useState<Boolean>(true)
    const policySortHandle = () => {
        setPolicySort(prevState => !prevState)
    }
    useEffect(() => {
        setFilteredData(prevState => {
            return [...prevState].sort((a, b) => {
                const policyComparison = policySort ? a.policy.localeCompare(b.policy) : b.policy.localeCompare(a.policy)
                return policyComparison
            })
        })
    }, [policySort])

    useEffect(() => {
        setSelectedCodes(prev => {
            const updated = new Set(prev)
            updated.clear()
            props.selectedPolicies.forEach(item => updated.add(item))
            return updated
        });

        selectedDataRef.current = props.data.filter(policy =>
            props.selectedPolicies.includes(policy.policy ?? '')
        )
    }, [props.data, props.selectedPolicies])

    const paginationList = (data: Policy[], size: number): Policy[][] => {
        const result: Policy[][] = []
        
        if (size >= data.length) {
            return [data]
        }

        let start = 0

        while (start < data.length) {
            result.push(data.slice(start, start + size))
            start += size
        }

        return result
    }

    const paginationNavigation = (side: "next" | "prev") => {
        if (side === "next") {
            if (slicedData.length > selectedPage + 1) {
                setSelectedPage(prevState => prevState + 1)
            }
            
        } else if (side === "prev") {
            if (0 <= selectedPage - 1){
                setSelectedPage(prevState => prevState - 1)
            }
        }
    }

    // Handle slice count changed
    const handleSliceCountChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSliceCount(event.target.valueAsNumber)
    }

    // handle search text change
    const handleSearchTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.trim().toLowerCase();
        if (event.target.value === '') {
            setFilteredData(props.data)
        } else {
            setFilteredData(prevState => prevState.filter(item => (item.policy?.toLowerCase()?.includes(value) ?? false)))
        }
    }

    useEffect(() => {
        setFilteredData(prevState => {
            return [...props.data].sort((a, b) => {
                const policyComparison = policySort ? a.policy.localeCompare(b.policy) : b.policy.localeCompare(a.policy)
                return policyComparison
            })
        })
    }, [props.data])
    

    useEffect(() => {
        if (rowNumber.current){
            setSlicedData(prevState => paginationList(filteredData, sliceCount))
        }
    }, [sliceCount, filteredData])

    const selectedDataRef = useRef<Policy[]>([])

    /* const handleSelectRow = (event: ChangeEvent<HTMLInputElement>, row: Policy) => {
        setSelectedCodes(prev => {
            const updated = new Set(prev)
            if (event.target.checked) 
            {
                updated.add(event.target.name)
            } 
            else 
            {
                updated.delete(event.target.name)
            }
            return updated
        })

        if (event.target.checked) 
        {
            selectedDataRef.current.push(row)
        } 
        else 
        {
            selectedDataRef.current = selectedDataRef.current.filter(item => item.value !== row.value)
            //setSelectedData(prevState => prevState.filter(item => item.value !== row.value))
        }
        props.handleSelectRow(selectedDataRef.current)
    } */
    
    const handleSelectRow = (event: ChangeEvent<HTMLInputElement>, row: Policy) => {
        setSelectedCodes(prev => {
            const updated = new Set(prev);

            if (row.value === "bothearlywarningsimple:manage" || row.value === "bothearlywarningadvance:manage") {
                // Remove only the opposite one, not all selections
                updated.delete("Both Early Warning Trigger - Simple");
                updated.delete("Both Early Warning Trigger - Advance");

                selectedDataRef.current = selectedDataRef.current.filter(
                    item =>
                        item.value !== "bothearlywarningsimple:manage" &&
                        item.value !== "bothearlywarningadvance:manage"
                );

                if (event.target.checked) {
                    updated.add(row.policy ?? "");
                    selectedDataRef.current.push(row);
                }
            } else {
                // Normal behavior for all others
                if (event.target.checked) {
                    updated.add(row.policy ?? "");
                    selectedDataRef.current.push(row);
                } else {
                    updated.delete(row.policy ?? "");
                    selectedDataRef.current = selectedDataRef.current.filter(item => item.value !== row.value);
                }
            }

            props.handleSelectRow([...selectedDataRef.current]); // sync with parent
            return updated;
        });
    };

    const handleSelectAllCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked

        setSelectedCodes(prev => {
            const updated = new Set(prev);
    
            if (isChecked) {
                // Add all policies from the filtered data
                filteredData.forEach(row => {
                    updated.add(row.policy ?? "");
                });
    
                // Update selectedDataRef with all filteredData
                selectedDataRef.current = [...filteredData];
            } else {
                // Clear all selections
                updated.clear();
                selectedDataRef.current = [];
            }
    
            // Sync with parent
            props.handleSelectRow([...selectedDataRef.current]);
            return updated;
        });
    }

    return (
        <div className='subTable-wrapper' style={{width: '100%'}}>
            <span className='title'>User polices</span>
            <div className='field-wrapper'>
                <div className='left'>
                    <span>show <input type="number" ref={rowNumber} defaultValue={10} min={10} onChange={(e) => handleSliceCountChange(e)}/> entries</span>
                </div>
                <div className='right'>
                    <span> search <input type="text" ref={searchText} onChange={(e) => handleSearchTextChange(e)} placeholder='Search here'/></span>
                </div>
            </div>
            <div className='table-body' style={{width: '100%'}}>
                <div className='table-wrapper'>
                    <div className='table-row header' style={{gridTemplateColumns: `3rem 1fr`, width: '100%'}}>
                        <span>
                            <input type="checkbox" className='select-all-checkbox' onChange={(e) => handleSelectAllCheckbox(e)}/>
                        </span>
                        <span onClick={(e) => policySortHandle()}>
                            Policies
                            <div>
                                {policySort ? (
                                    <UpIcon width="1rem" color="var(--color-main-white)" />
                                ) : (
                                    <DownIcon width="1rem" color="var(--color-main-white)" />
                                )}
                            </div>
                        </span>
                    </div>
                </div>

                {props.isDataLoading ? (
                    <div className='loading-wrapper'>
                        <img src={theme === 'dark' ? loadDarkIcon : theme === 'light' ? loadLightIcon : theme === 'colorful' ? loadLightIcon : ''} alt="" />
						<span>Searching policies...</span>
                    </div>
                ) : (
                    slicedData[selectedPage] && slicedData[selectedPage].map((row, index) => (
                        <div key={index} className='table-wrapper'>
                            <div className='table-row' style={{gridTemplateColumns: `3rem 1fr`, width: '100%'}}>
                                <span style={{justifyContent: 'center'}}>
                                    <input type="checkbox" name={`${row.policy}`} onChange={(e) => handleSelectRow(e, row)} checked={selectedCodes.has(row.policy)}/>
                                    </span>
                                <span>{row.policy}</span>
                            </div>
                        </div>
                    ))
                )}
                
            </div>
            
            <div className='table-pagination-wrapper'>
                <div className='icon-page-box' style={{marginBottom: "0.5rem"}} onClick={(e) => paginationNavigation("prev")}>&lt;</div>
                <div className='page-wrapper'>
                    {slicedData.map((page, index) => (
                        <span key={index} className={selectedPage === index ? 'icon-page-box selected' : 'icon-page-box'} onClick={(e) => setSelectedPage(index)}>{index + 1}</span>
                    ))}
                </div>
                <div className='icon-page-box' style={{marginBottom: "0.5rem"}} onClick={(e) => paginationNavigation("next")}>&gt;</div>
            </div>
        </div>
    )
}

export default OrganizationPolicyDataTable